import { rem } from 'polished';
import { theme as baseTheme } from 'roo-ui';
import * as icons from './icons';
import merge from 'lodash/merge';

// based on qantas theme - https://github.com/hooroo/roo-ui/blob/master/src/theme.js

export const BREAK_POINTS_PX = [768, 1100, 1280];

const theme = merge({}, baseTheme, {
  maxWidths: {
    default: rem('1260px'),
    narrow: rem('650px'),
    sidebar: rem('280px'),
  },
  fontSizes: {
    xs: rem('15px'),
    sm: rem('16px'),
    base: rem('18px'),
    md: rem('20px'),
    lg: rem('24px'),
    xl: rem('32px'),
    '2xl': rem('34px'),
    '3xl': rem('36px'),
    '4xl': rem('46px'),
  },
  focus: {
    outline: `2px solid ${baseTheme.colors.brand.secondary}`,
    boxShadow: `0 0 0 2px ${baseTheme.colors.brand.secondary}`,
  },
  breakpoints: BREAK_POINTS_PX.map((bp) => `${bp}px`),
  mediaQueries: BREAK_POINTS_PX.map((bp) => `@media screen and (min-width: ${bp}px)`),
  shadows: {
    soft: '0 1px 20px rgba(0, 0, 0, 0.15)',
    hard: '0 1px 2px 0 rgba(0, 0, 0, 0.2);',
    modal: '0 4px 10px 0 rgba(0, 0, 0, 0.1)',
    result: '0 5px 10px 0 rgba(0, 0, 0, 0.1)',
    footer: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
    small: '0 0 8px 0 rgba(0,0,0,0.1)',
    hover: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
    tooltip: '0px 0px 15px 0px rgba(0, 0, 0, 0.1)',
  },
  uiStructure: {
    phoneMenuDrawerCloseWidth: rem('64px'),
    mediumScreenSidebar: rem('400px'),
    tooltipWidth: rem('540px'),
  },
  zIndices: {
    dropdownContent: 2,
    highlightBorder: 50,
    header: 100,
    tooltip: 103,
    imageGallery: 150,
    mobileModal: 201,
    modalContent: 202,
    mapsPage: 300,
    stickyPaymentOptions: 475,
    stickyNavigation: 480,
    stickyCampaignMessaging: 490,
    searchControls: 491,
    loginToolTip: 490,
    modal: 500,
    phoneMenuDrawer: 501,
    phoneMenuDrawerClose: 502,
    navigationDropdown: 503,
  },
  radii: {
    default: '4px',
    exagerated: '8px',
    defaultRoundRightOnly: '0 4px 4px 0',
    defaultRoundLeftOnly: '4px 0 0 4px',
    defaultRoundTopOnly: '4px 4px 0 0',
    defaultRoundBottomOnly: '0 0 4px 4px',
    exageratedRoundRightOnly: '0 8px 8px 0',
    exageratedRoundLeftOnly: '8px 0 0 8px',
    exageratedRoundTopOnly: '8px 8px 0 0',
    exageratedRoundBottomOnly: '0 0 8px 8px',
  },
  imageSizes: {
    mapSearchResult: {
      height: '100%',
      width: rem('135px'),
    },
    mapSearchResultPhone: {
      height: '100%',
      width: rem('96px'),
    },
    searchThumbnail: {
      width: [rem('135px'), rem('242px')],
      height: ['100%', rem('225px')],
    },
    legacyRoomTypeThumbnail: {
      width: ['100%', rem('200px')],
      height: rem('140px'),
    },
    roomTypeThumbnail: {
      width: ['100%', rem('278px')],
      height: rem('180px'),
    },
    propertyDescription: {
      width: ['100%', rem('720px'), rem('812px')],
      height: [rem('195px'), rem('460px')],
    },
  },
  colors: {
    snow: '#fbfbfb',
    semiTransparentDark: 'rgba(0, 0, 0, 0.9)',
    external: {
      trivago: '#37454d',
      tripAdvisor: '#00a680',
      google: '#4885ed',
    },
  },
  icons,
  slider: {
    rail: {
      height: '3px',
      background: baseTheme.colors.greys.alto,
    },
    track: {
      background: baseTheme.colors.brand.primary,
      height: '3px',
    },
    handle: {
      border: `2px solid ${baseTheme.colors.brand.primary}`,
      background: baseTheme.colors.brand.primary,
      width: '24px',
      height: '24px',
      top: '-1px',
    },
  },
  truncateTextGreyBackground: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(244,245,246, 1) 90%)',
});

const variations = {
  textStyles: {
    h1: {
      fontSize: theme.fontSizes.xl,
    },
  },
  buttons: {
    inverted: {
      color: theme.colors.brand.primary,
      backgroundColor: theme.colors.white,
    },
    menuButton: {
      color: theme.colors.greys.charcoal,
      backgroundColor: theme.colors.white,
      border: theme.borders[1],
      borderColor: theme.colors.greys.alto,
      fontSize: theme.fontSizes.base,
      fontWeight: theme.fontWeights.normal,
      lineHeight: theme.lineHeights.tight,
      letterSpacing: 'normal',
      textTransform: 'none',
      '&&:hover:not(:disabled)': {
        borderColor: theme.colors.greys.alto,
        backgroundColor: theme.colors.white,
      },
      '&:active': {
        borderColor: theme.colors.brand.secondary,
        backgroundColor: theme.colors.ui.infoBackground,
      },
      '&:focus': {
        borderColor: theme.colors.brand.secondary,
        backgroundColor: theme.colors.ui.infoBackground,
      },
    },
    mobilePillButton: {
      border: 0,
      padding: 0,
      backgroundColor: 'transparent',
      textTransform: 'none',
      letterSpacing: 'normal',
      color: theme.colors.white,
      minWidth: '126px',
      height: '48px',
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
    },
  },
};

export default merge({}, theme, variations);
